import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  CircularProgress,
  Link,
  IconButton,
  Tooltip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LaunchIcon from '@mui/icons-material/Launch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadIcon from '@mui/icons-material/Upload';
import DownloadIcon from '@mui/icons-material/Download';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
 
import { apiService, AdDetail, licensingAPI, License } from '../services/api';
import { AdMetadata, getMeantToPlatforms } from '../types';
import ApplovinUploadDialog from '../components/ApplovinUploadDialog';
import SlackUserSelector from '../components/common/SlackUserSelector';
import LicenseSelector from '../components/common/LicenseSelector';
import LicenseStatusBadge from '../components/common/LicenseStatusBadge';
import AdJobsSection from '../components/AdJobsSection';
import GoogleAdsAssociationDialog from '../components/GoogleAdsAssociationDialog';
import PerformanceSummary from '../components/common/PerformanceSummary';
import { useAuth } from '../contexts/AuthContext';
import Hls from 'hls.js';
import { AD_LANGUAGE_OPTIONS } from '../constants/languages';

const GOOGLE_ADS_UPLOAD_ENABLED = process.env.REACT_APP_GOOGLE_ADS_UPLOAD_ENABLED !== 'false';

// Form validation schema for metadata editing
const editMetadataSchema: yup.ObjectSchema<EditMetadataFormData> = yup.object({
  language: yup.string().required('Language is required'),
  watchers: yup.array().of(yup.string().defined()).default([]),
  cast: yup.string().nullable().defined().default(null),
  heroAge: yup.string().nullable().defined().default(null),
  heroGender: yup.string().nullable().defined().default(null),
  meantTo: yup.array().of(yup.string().defined()).default([]),
});

type EditMetadataFormData = {
  language: string;
  watchers: string[];
  cast: string | null;
  heroAge: string | null;
  heroGender: string | null;
  meantTo: string[];
};

const META_UPLOAD_ENABLED = (process.env.REACT_APP_META_UPLOAD_ENABLED || 'true').toLowerCase() === 'true';

type AdTags = NonNullable<AdMetadata['tags']>;

const CAST_TAG_VALUES: Record<string, true> = { Kids: true, Couple: true, 'Solo actor': true, 'No Human actor': true, 'Human with Prompt': true, 'hand only': true, Family: true, unknown: true };
const HERO_AGE_VALUES: Record<string, true> = { Senior: true, Adult: true, Teenage: true, Kid: true, unknown: true };
const HERO_GENDER_VALUES: Record<string, true> = { Female: true, Male: true, other: true, unknown: true };

const getObjectField = (value: unknown, field: string): unknown => {
  if (!value || typeof value !== 'object' || !(field in value)) return undefined;
  const record = value as Record<string, unknown>;
  return record[field];
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

// Google Ads assets can never be deleted, so an orphaned asset id is the user's only record of a
// permanent asset. `orphanedAssetId` is set only for GOOGLE_ADS_ASSET_ORPHANED.
type GoogleAdsErrorState = {
  message: string;
  orphanedAssetId: string | null;
};

const ORPHANED_ASSET_ID_PATTERN = /\basset\s+(\d+)/i;

const getOrphanedAssetId = (detail: string): string | null => {
  const match = ORPHANED_ASSET_ID_PATTERN.exec(detail);
  return match ? match[1] : null;
};

const getGoogleAdsUploadErrorMessage = (error: unknown): string => {
  const detail = getErrorMessage(error, 'Failed to add to Google Ads library');
  const code = getObjectField(error, 'code');

  if (code === 'INVALID_MEDIA_BUNDLE') return `This HTML bundle is invalid, so Google Ads cannot accept it. ${detail}`;
  if (code === 'GOOGLE_ADS_ASSET_REJECTED') return `Google Ads rejected this creative. ${detail}`;
  // Explicit branch: the backend detail carries the id of an undeletable asset, so it must never be
  // shortened or replaced by generic guidance.
  if (code === 'GOOGLE_ADS_ASSET_ORPHANED') return detail;
  return detail;
};

const isCastTag = (value: unknown): value is AdTags['cast'] => typeof value === 'string' && CAST_TAG_VALUES[value] === true;
const isHeroAgeTag = (value: unknown): value is AdTags['heroAge'] => typeof value === 'string' && HERO_AGE_VALUES[value] === true;
const isHeroGenderTag = (value: unknown): value is AdTags['heroGender'] => typeof value === 'string' && HERO_GENDER_VALUES[value] === true;


const AdDetailPage: React.FC = () => {
  const { adId } = useParams<{ adId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isAuthorized } = useAuth();
  const [ad, setAd] = useState<AdDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [youtubeUploading, setYoutubeUploading] = useState(false);
  const [metaUploading, setMetaUploading] = useState(false);
  const [applovinUploading, setApplovinUploading] = useState(false);
  const [applovinDialogOpen, setApplovinDialogOpen] = useState(false);
  const [googleAdsInFlightAdIds] = useState(() => new Set<string>());
  const [, setGoogleAdsUploadStateVersion] = useState(0);
  const googleAdsAdding = Boolean(adId && googleAdsInFlightAdIds.has(adId));
  const [googleAdsError, setGoogleAdsError] = useState<GoogleAdsErrorState | null>(null);
  const googleAdsRouteAdIdRef = useRef(adId);
  const googleAdsRequestVersionRef = useRef(0);
  const routeGenerationRef = useRef(0);
  if (googleAdsRouteAdIdRef.current !== adId) routeGenerationRef.current += 1;
  const routeGeneration = routeGenerationRef.current;
  const mountedRef = useRef(false);
  const settledGoogleAdsAssetsRef = useRef(new Map<string, string>());
  googleAdsRouteAdIdRef.current = adId;
  const [googleAdsAssociationOpen, setGoogleAdsAssociationOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [fullTranscript, setFullTranscript] = useState<string | null>(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [isTogglingDisabled, setIsTogglingDisabled] = useState(false);

  // Debug modal state
  const [isDebugModalOpen, setIsDebugModalOpen] = useState(false);
  const [debugData, setDebugData] = useState<any>(null);

  // Video player state for buffering/stalling
  const [videoStalled, setVideoStalled] = useState(false);
  const stalledRetryCountRef = useRef(0);
  // Identifies the ad the route currently points at. An association save resolves asynchronously,
  // so it compares against this to tell whether its ad is still the one on screen.
  const currentAdIdRef = useRef(adId);
  currentAdIdRef.current = adId;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      googleAdsRequestVersionRef.current += 1;
    };
  }, []);

  // Form setup for edit metadata modal
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditMetadataFormData>({
    resolver: yupResolver(editMetadataSchema),
    defaultValues: {
      language: 'EN',
      watchers: [],
      cast: null,
      heroAge: null,
      heroGender: null,
      meantTo: [],
    },
  });


  // Licensing state
  const [adLicenses, setAdLicenses] = useState<License[]>([]);
  const [licensesLoading, setLicensesLoading] = useState(false);
  const [showLicenseSelector, setShowLicenseSelector] = useState(false);

  // Video player refs and telemetry
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsInstanceRef = useRef<Hls | null>(null);
  const startupStartRef = useRef<number | null>(null);

  const getAdContentType = (candidate: AdDetail): string => {
    const fileMetadata = getObjectField(candidate.metadata, 'file_metadata');
    const contentType = getObjectField(fileMetadata, 'content_type');
    return typeof contentType === 'string' ? contentType.toLowerCase() : '';
  };

  const getAdMediaType = (candidate: AdDetail): string => {
    const mediaType = getObjectField(candidate.metadata, 'media_type');
    return typeof mediaType === 'string' ? mediaType.toLowerCase() : '';
  };

  const isHtmlAd = (candidate: AdDetail): boolean => {
    const url = candidate.video_url || '';
    return getAdMediaType(candidate) === 'html'
      || getAdContentType(candidate).startsWith('text/html')
      || /\.(html|htm)$/i.test(url.split('?', 1)[0]);
  };

  const isImageAd = (candidate: AdDetail): boolean => {
    const url = candidate.video_url || '';
    return getAdMediaType(candidate) === 'image'
      || getAdContentType(candidate).startsWith('image/')
      || /\.(jpg|jpeg|png)$/i.test(url.split('?', 1)[0]);
  };

  const isVideoAd = (candidate: AdDetail): boolean => !isHtmlAd(candidate) && !isImageAd(candidate);


  // Setup video source, HLS, and basic telemetry when URL changes
  useEffect(() => {
    const videoUrl = ad?.video_url || '';

    // Only handle video setup here; images and HTML are rendered directly below
    if (!videoRef.current || !videoUrl || !ad || !isVideoAd(ad)) {
      return () => {};
    }

    const videoEl = videoRef.current;

    // Cleanup helper
    const cleanup = () => {
      // Detach HLS if used
      if (hlsInstanceRef.current) {
        try {
          hlsInstanceRef.current.destroy();
        } catch {}
        hlsInstanceRef.current = null;
      }
      // Remove all listeners we added
      videoEl.removeEventListener('loadedmetadata', onLoadedMetadata);
      videoEl.removeEventListener('loadeddata', onLoadedData);
      videoEl.removeEventListener('canplay', onCanPlay);
      videoEl.removeEventListener('canplaythrough', onCanPlayThrough);
      videoEl.removeEventListener('playing', onPlaying);
      videoEl.removeEventListener('waiting', onWaiting);
      videoEl.removeEventListener('stalled', onStalled);
      videoEl.removeEventListener('error', onError);
      videoEl.removeEventListener('seeking', onSeeking);
      videoEl.removeEventListener('seeked', onSeeked);
    };

    // Telemetry handlers
    const logPrefix = `[VideoPlayer ${ad?.ad_id || ''}]`;
    const markStart = () => {
      startupStartRef.current = performance.now();
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} loadstart`);
    };
    const logStartup = (label: string) => {
      if (startupStartRef.current != null) {
        const ms = Math.round(performance.now() - startupStartRef.current);
        // eslint-disable-next-line no-console
        console.log(`${logPrefix} ${label} after ${ms} ms`);
      } else {
        // eslint-disable-next-line no-console
        console.log(`${logPrefix} ${label}`);
      }
    };
    const onLoadedMetadata = () => logStartup('loadedmetadata');
    const onLoadedData = () => logStartup('loadeddata');
    const onCanPlay = () => logStartup('canplay');
    const onCanPlayThrough = () => logStartup('canplaythrough');
    const onPlaying = () => {
      logStartup('playing');
      // Clear stalled state when playback resumes
      setVideoStalled(false);
      stalledRetryCountRef.current = 0;
    };
    const onWaiting = () => {
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} waiting at t=${videoEl.currentTime.toFixed(2)}s`);
      setVideoStalled(true);
    };
    const onStalled = () => {
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} stalled at t=${videoEl.currentTime.toFixed(2)}s`);
      setVideoStalled(true);
      
      // Retry logic: attempt to reload once per stall event
      if (stalledRetryCountRef.current < 3) {
        stalledRetryCountRef.current += 1;
        // eslint-disable-next-line no-console
        console.log(`${logPrefix} attempting reload (retry ${stalledRetryCountRef.current}/3)`);
        setTimeout(() => {
          try {
            videoEl.load();
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`${logPrefix} reload failed:`, e);
          }
        }, 500);
      }
    };
    const onError = () => {
      const mediaError = (videoEl.error && videoEl.error.message) || 'unknown';
      // eslint-disable-next-line no-console
      console.warn(`${logPrefix} error: ${mediaError}`);
    };
    const onSeeking = () => {
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} seeking to t=${videoEl.currentTime.toFixed(2)}s`);
    };
    const onSeeked = () => {
      // eslint-disable-next-line no-console
      console.log(`${logPrefix} seeked at t=${videoEl.currentTime.toFixed(2)}s`);
    };

    // Attach listeners
    videoEl.addEventListener('loadedmetadata', onLoadedMetadata);
    videoEl.addEventListener('loadeddata', onLoadedData);
    videoEl.addEventListener('canplay', onCanPlay);
    videoEl.addEventListener('canplaythrough', onCanPlayThrough);
    videoEl.addEventListener('playing', onPlaying);
    videoEl.addEventListener('waiting', onWaiting);
    videoEl.addEventListener('stalled', onStalled);
    videoEl.addEventListener('error', onError);
    videoEl.addEventListener('seeking', onSeeking);
    videoEl.addEventListener('seeked', onSeeked);

    // Begin setup
    markStart();

    const isHlsUrl = /\.m3u8(\?|$)/i.test(videoUrl);
    const canPlayNativeHls = videoEl.canPlayType('application/vnd.apple.mpegURL');

    if (isHlsUrl) {
      if (canPlayNativeHls) {
        videoEl.src = videoUrl;
      } else if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
        });
        hlsInstanceRef.current = hls;
        hls.loadSource(videoUrl);
        hls.attachMedia(videoEl);
        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // eslint-disable-next-line no-console
          console.log(`${logPrefix} HLS manifest parsed`);
        });
        hls.on(Hls.Events.ERROR, (_event: unknown, data: { type: string; details: string }) => {
          // eslint-disable-next-line no-console
          console.warn(`${logPrefix} HLS error: ${data.type} - ${data.details}`);
        });
      } else {
        // Fallback: attempt direct src (may fail on some browsers)
        videoEl.src = videoUrl;
      }
    } else {
      // MP4/others: direct source
      videoEl.src = videoUrl;
    }

    return cleanup;
  }, [ad]);

  useEffect(() => {
    googleAdsRequestVersionRef.current += 1;
    setGoogleAdsAssociationOpen(false);
    setGoogleAdsError(null);
    setSuccessMessage(null);
    setErrorMessage(null);

    const load = async () => {
      if (!adId) return;
      const requestedAdId = adId;
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getAd(requestedAdId);
        if (mountedRef.current && routeGenerationRef.current === routeGeneration) {
          const assetId = settledGoogleAdsAssetsRef.current.get(requestedAdId);
          setAd(assetId && !data.google_ads_asset_id ? { ...data, google_ads_asset_id: assetId } : data);
        }
      } catch (e: unknown) {
        if (mountedRef.current && routeGenerationRef.current === routeGeneration) {
          setError(getErrorMessage(e, 'Failed to load ad'));
        }
      } finally {
        if (mountedRef.current && routeGenerationRef.current === routeGeneration) setLoading(false);
      }
    };
    load();
  }, [adId, routeGeneration]);

  useEffect(() => {
    const loadLicenses = async () => {
      if (!adId) return;
      setLicensesLoading(true);
      try {
        const { licenses } = await licensingAPI.getAdLicenses(adId);
        setAdLicenses(licenses);
      } catch {
        setAdLicenses([]);
      } finally {
        setLicensesLoading(false);
      }
    };
    loadLicenses();
  }, [adId]);

  const handleLinkLicense = async (licenseIds: string[]) => {
    if (!adId) return;
    const currentIds = adLicenses.map(l => l.license_id);
    const toAdd = licenseIds.filter(id => !currentIds.includes(id));
    const toRemove = currentIds.filter(id => !licenseIds.includes(id));

    try {
      for (const id of toAdd) {
        await licensingAPI.link(id, 'ad', adId);
      }
      for (const id of toRemove) {
        await licensingAPI.unlink(id, adId);
      }
      const { licenses } = await licensingAPI.getAdLicenses(adId);
      setAdLicenses(licenses);
      setShowLicenseSelector(false);
      setSuccessMessage('Licenses updated');
    } catch (e: unknown) {
      setErrorMessage(getErrorMessage(e, 'Failed to update licenses'));
    }
  };

  const handleUnlinkLicense = async (licenseId: string) => {
    if (!adId) return;
    try {
      await licensingAPI.unlink(licenseId, adId);
      setAdLicenses(prev => prev.filter(l => l.license_id !== licenseId));
      setSuccessMessage('License removed');
    } catch (e: unknown) {
      setErrorMessage(getErrorMessage(e, 'Failed to remove license'));
    }
  };

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'uploaded': return 'info';
      case 'reviewed': return 'warning';
      case 'approved': return 'success';
      case 'published': return 'primary';
      default: return 'default';
    }
  };

  const handleYouTubeUpload = async () => {
    if (!adId || !ad || youtubeUploading) return; // Prevent double-clicks

    setYoutubeUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await apiService.uploadAdToYouTube(adId);
      setSuccessMessage(`YouTube upload initiated successfully! Job ID: ${result.job_id}`);
      
      // Store job ID for potential status checking
      console.log(`YouTube upload job created: ${result.job_id}`);
      
      // Optionally refresh the ad data to get updated status
      // You might want to poll for job status updates here
      
    } catch (error: unknown) {
      console.error('YouTube upload failed:', error);
      setErrorMessage(getErrorMessage(error, 'Failed to initiate YouTube upload'));
    } finally {
      setYoutubeUploading(false);
    }
  };

  const handleMetaUpload = async () => {
    if (!adId || !ad || metaUploading) return; // Prevent double-clicks

    setMetaUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await apiService.uploadAdToMeta(adId);
      setSuccessMessage(`Meta Ad Library upload initiated successfully! Job ID: ${result.job_id}`);
      
      // Store job ID for potential status checking
      console.log(`Meta upload job created: ${result.job_id}`);
      
      // Optionally refresh the ad data to get updated status
      // You might want to poll for job status updates here
      
    } catch (error: unknown) {
      console.error('Meta upload failed:', error);
      setErrorMessage(getErrorMessage(error, 'Failed to initiate Meta upload'));
    } finally {
      setMetaUploading(false);
    }
  };

  const handleApplovinUpload = async (templateCreativeSetId: string, campaignId: string) => {
    if (!adId || !ad || applovinUploading) return;

    setApplovinDialogOpen(false);
    setApplovinUploading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await apiService.uploadAdToApplovin(adId, templateCreativeSetId, campaignId);
      setSuccessMessage(`AppLovin upload initiated. Processing...`);

      const jobId = result.job_id;
      const maxAttempts = 60;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const job = await apiService.getJobStatus(jobId);
          if (job.status === 'completed') {
            const refreshedAd = await apiService.getAd(adId);
            setAd(refreshedAd);
            const warning = job.platform_response?.warning;
            if (warning) {
              setSuccessMessage(`AppLovin upload completed with warning: ${warning}`);
            } else {
              setSuccessMessage('AppLovin upload completed successfully!');
            }
            return;
          } else if (job.status === 'failed') {
            setErrorMessage(`AppLovin upload failed: ${job.error_message || 'Unknown error'}`);
            return;
          }
        } catch {
          // Polling error — keep trying
        }
      }
      setErrorMessage('AppLovin upload timed out. Check the Jobs page for status.');
    } catch (error: unknown) {
      console.error('AppLovin upload failed:', error);
      setErrorMessage(getErrorMessage(error, 'Failed to initiate AppLovin upload'));
    } finally {
      setApplovinUploading(false);
    }
  };

  const handleAddToGoogleAds = async () => {
    if (!adId || !ad || googleAdsInFlightAdIds.has(adId)) return;

    const requestAdId = adId;
    const requestVersion = ++googleAdsRequestVersionRef.current;
    const isCurrentRequest = () => (
      mountedRef.current
      && routeGenerationRef.current === routeGeneration
      && googleAdsRouteAdIdRef.current === requestAdId
      && googleAdsRequestVersionRef.current === requestVersion
    );

    googleAdsInFlightAdIds.add(requestAdId);
    setGoogleAdsUploadStateVersion((version) => version + 1);
    setGoogleAdsError(null);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await apiService.addToGoogleAds(requestAdId);
      if (result.google_ads_asset_id) {
        settledGoogleAdsAssetsRef.current.set(requestAdId, result.google_ads_asset_id);
      }
      if (!isCurrentRequest()) return;

      if (result.status === 'already_exists') {
        setSuccessMessage('Ad is already in Google Ads library');
      } else {
        setSuccessMessage(`Uploaded to Google Ads library. Asset ID: ${result.google_ads_asset_id}`);
      }

      // Refresh in its own try/catch: the upload already succeeded, so a refresh failure must not be
      // reported as an upload failure.
      try {
        const refreshedAd = await apiService.getAd(requestAdId);
        if (isCurrentRequest()) setAd(refreshedAd);
      } catch (refreshError: unknown) {
        console.error('Google Ads upload succeeded for ad %s but refreshing the ad failed:', requestAdId, refreshError);
        if (isCurrentRequest()) {
          setGoogleAdsError({
            message: 'Uploaded, but the page could not refresh. Reload the page to see the Google Ads asset id on this ad.',
            orphanedAssetId: null,
          });
        }
      }
    } catch (error: unknown) {
      const code = getObjectField(error, 'code');
      const message = getGoogleAdsUploadErrorMessage(error);
      const orphanedAssetId = code === 'GOOGLE_ADS_ASSET_ORPHANED' ? getOrphanedAssetId(message) : null;
      if (code === 'GOOGLE_ADS_ASSET_ORPHANED') {
        // Google Ads assets cannot be deleted, and the ad row does not know this id, so this console
        // line may be the only surviving record of the asset if the user dismisses the alert.
        console.error(
          'Add to Google Ads left an orphaned asset: code=GOOGLE_ADS_ASSET_ORPHANED ad=%s assetId=%s',
          requestAdId, orphanedAssetId || 'unknown', error,
        );
      } else {
        console.error('Add to Google Ads failed for ad %s:', requestAdId, error);
      }
      if (isCurrentRequest()) setGoogleAdsError({ message, orphanedAssetId });
    } finally {
      googleAdsInFlightAdIds.delete(requestAdId);
      if (mountedRef.current) {
        // A returning route owns newer metadata. Reconcile only the confirmed asset, including
        // when the upload's original refresh belonged to an earlier visit to this same ad.
        const assetId = settledGoogleAdsAssetsRef.current.get(requestAdId);
        if (!isCurrentRequest() && currentAdIdRef.current === requestAdId && assetId) {
          setAd((current) => current?.ad_id === requestAdId && !current.google_ads_asset_id
            ? { ...current, google_ads_asset_id: assetId }
            : current);
        }
        setGoogleAdsUploadStateVersion((version) => version + 1);
      }
    }
  };

  const generateYouTubeUrl = (videoId: string): string => {
    return `https://www.youtube.com/watch?v=${videoId}`;
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccessMessage(`${label} copied to clipboard`);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setErrorMessage('Failed to copy to clipboard');
    }
  };

  const handleDownloadFile = async () => {
    if (!ad || !adId) return;
    
    try {
      setSuccessMessage('Preparing download...');
      
      // Extract file extension from URL
      const extMatch = ad.video_url.match(/\.(mp4|mov|avi|mkv|webm|jpg|jpeg|png|html|htm)(?:\?|$)/i);
      const ext = extMatch ? extMatch[1].toLowerCase() : isHtmlAd(ad) ? 'html' : 'mp4';
      
      // Fetch the file as a blob to avoid CORS issues with download attribute
      // For pre-signed URLs, we need to use 'no-cors' mode or handle CORS properly
      const response = await fetch(ad.video_url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit',
        headers: {
          'Origin': window.location.origin
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Create blob URL and trigger download
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      // Use original ad_id without encoding for the filename
      link.download = `${ad.ad_id}.${ext}`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      
      setSuccessMessage('Download started');
    } catch (error: unknown) {
      console.error('Download failed:', error);
      setErrorMessage(getErrorMessage(error, 'Failed to download file'));
    }
  };

  const handleToggleDisabled = async () => {
    if (!adId || !ad || isTogglingDisabled) return;

    setIsTogglingDisabled(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const newDisabledState = !ad.disabled;
      await apiService.toggleAdDisabled(adId, newDisabledState);
      setSuccessMessage(`Ad ${newDisabledState ? 'disabled' : 'enabled'} successfully`);
      
      // Update local ad state
      setAd({ ...ad, disabled: newDisabledState });
    } catch (error: unknown) {
      console.error('Toggle disabled failed:', error);
      setErrorMessage(getErrorMessage(error, 'Failed to toggle ad disabled status'));
    } finally {
      setIsTogglingDisabled(false);
    }
  };

  const handleOpenEditModal = () => {
    if (!ad) return;
    
    // Extract meantTo array
    const meantToRaw = ad.tags?.meantTo as unknown;
    const meantToArray: string[] = Array.isArray(meantToRaw) ? meantToRaw : [];

    reset({
      language: ad.language || 'EN',
      watchers: ad.watchers || [],
      cast: (ad.tags?.cast as string) || null,
      heroAge: (ad.tags?.heroAge as string) || null,
      heroGender: (ad.tags?.heroGender as string) || null,
      meantTo: meantToArray,
    });
    
    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setErrorMessage(null);
  };

  const handleOpenDebugModal = () => {
    if (!ad) return;
    setDebugData(ad);
    setIsDebugModalOpen(true);
  };

  const handleCloseDebugModal = () => {
    setIsDebugModalOpen(false);
    setDebugData(null);
  };

  // Generate USPs for this ad
  const handleGenerateUsps = async () => {
    if (!ad) return;

    try {
      setLoading(true);
      setErrorMessage(null); // Clear any previous errors

      const result = await apiService.generateUspsForAd(ad.ad_id);

      if (result.usps && result.usps.length > 0) {
        setSuccessMessage(`✅ Generated ${result.usps.length} USPs for this ad`);
        // Refresh ad data to show new USPs
        window.location.reload();
      } else {
        setErrorMessage(`⚠️ ${result.message || 'USP generation completed'}`);
      }
    } catch (error: unknown) {
      setErrorMessage(`❌ Failed to generate USPs: ${getErrorMessage(error, 'Unknown error')}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMetadata = async (formData: EditMetadataFormData) => {
    if (!adId) return;

    setIsUpdating(true);
    setErrorMessage(null);

    try {
      const payload: Partial<AdMetadata> = {
        language: formData.language,
        watchers: formData.watchers,
      };
      
      payload.tags = {
        cast: isCastTag(formData.cast) ? formData.cast : 'unknown',
        heroAge: isHeroAgeTag(formData.heroAge) ? formData.heroAge : 'unknown',
        heroGender: isHeroGenderTag(formData.heroGender) ? formData.heroGender : 'unknown',
        meantTo: formData.meantTo || []
      };

      const updatedAd = await apiService.updateAdMetadata(adId, payload);

      // Update the local ad state
      setAd(updatedAd);
      setSuccessMessage('Metadata updated successfully');
      setEditModalOpen(false);
    } catch (error: unknown) {
      console.error('Failed to update metadata:', error);
      setErrorMessage(getErrorMessage(error, 'Failed to update metadata'));
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading ad details...
        </Typography>
      </Container>
    );
  }

  if (error || !ad) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'Ad not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(isAuthenticated ? '/productions' : '/')}
        sx={{ mb: 3 }}
      >
        {isAuthenticated ? 'Back to Productions' : 'Back to Home'}
      </Button>

      {/* Disabled Overlay Banner */}
      {ad.disabled && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: 'rgba(237, 108, 2, 0.95)',
            backdropFilter: 'blur(2px)',
            py: 3,
            px: 4,
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            borderBottom: '4px solid #d84315',
          }}
        >
          <Container maxWidth="lg">
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px',
                  }}
                >
                  ⚠️
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                    AD DISABLED
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.95)' }}>
                    All upload options are blocked until this ad is re-enabled
                  </Typography>
                </Box>
              </Box>
              {isAuthenticated && isAuthorized && (
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: 'white',
                    color: '#ed6c02',
                    fontWeight: 700,
                    px: 4,
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                    },
                  }}
                  onClick={handleToggleDisabled}
                  disabled={isTogglingDisabled}
                >
                  {isTogglingDisabled ? <CircularProgress size={24} sx={{ color: '#ed6c02' }} /> : 'Enable Ad Now'}
                </Button>
              )}
            </Box>
          </Container>
        </Box>
      )}

      {/* Add padding when disabled banner is showing */}
      <Box sx={{ pt: ad.disabled ? '140px' : 0 }} />

      <Grid container spacing={3}>
        {/* Media Preview */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={2}
            sx={{ 
              opacity: ad.disabled ? 0.6 : 1,
              filter: ad.disabled ? 'grayscale(50%)' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            <Card>
              <Box sx={{ position: 'relative' }}>
                {isHtmlAd(ad) ? (
                  <iframe
                    title={ad.name_en || 'HTML creative'}
                    src={ad.video_url}
                    sandbox="allow-scripts allow-pointer-lock"
                    style={{ width: '100%', height: 400, border: 0, backgroundColor: '#111' }}
                  />
                ) : isImageAd(ad) ? (
                  <img
                    alt={ad.name_en || 'Ad image'}
                    style={{ width: '100%', height: 400, objectFit: 'contain', backgroundColor: '#111' }}
                    src={ad.video_url}
                  />
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <video
                      ref={videoRef}
                      controls
                      preload="metadata"
                      playsInline
                      crossOrigin="anonymous"
                      style={{ width: '100%', height: 400, objectFit: 'contain', backgroundColor: '#111' }}
                    />
                    {videoStalled && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          color: 'white',
                          padding: '16px 24px',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          pointerEvents: 'none',
                        }}
                      >
                        <CircularProgress size={20} sx={{ color: 'white' }} />
                        <Typography variant="body2">Reconnecting...</Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h5" component="h1">
                    {ad.name_en || 'Untitled Ad'}
                  </Typography>
                  <Chip label={ad.status} color={getStatusColor(ad.status)} />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {ad.ad_id}
                </Typography>
              </CardContent>
            </Card>
          </Paper>

          {/* Performance Section */}
          {ad.performance_updated_at && (
            <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
              <PerformanceSummary mode="ad" data={ad} />
            </Paper>
          )}

          {/* Tags Section */}
          <Paper 
            elevation={1} 
            sx={{ 
              p: 2, 
              mt: 2,
              opacity: ad.disabled ? 0.6 : 1,
              filter: ad.disabled ? 'grayscale(50%)' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            <Typography variant="h6">Tags</Typography>
            {(() => {
              const tags = ad.tags;
              const licensedSongsRaw: unknown = tags?.licensedSong;
              const licensedSongs: string[] = Array.isArray(licensedSongsRaw)
                ? licensedSongsRaw.filter((s): s is string => typeof s === 'string' && s.trim() !== '')
                : (typeof licensedSongsRaw === 'string' && licensedSongsRaw.trim() !== '' ? [licensedSongsRaw] : []);
              const meantTo: string[] = tags && Array.isArray(tags.meantTo) ? tags.meantTo : [];
              const audience: string[] = tags && Array.isArray(tags.audience) ? tags.audience : [];
              const seasonal: string[] = tags && Array.isArray(tags.seasonal) ? tags.seasonal : [];
              const contentShownInApp: string[] = tags && Array.isArray(tags.contentShownInApp) ? tags.contentShownInApp : [];

              const rows: { label: string; values: string[] }[] = [];

              // Add USPs if available
              const usps = ad.metadata?.usps?.usp_list as string[] | undefined;
              const uspStatus = ad.metadata?.usps?.status as string | undefined;
              const uspError = ad.metadata?.usps?.error as string | undefined;

              if (uspStatus === 'failed' && uspError) {
                rows.push({ label: 'USPs (Failed)', values: [`Error: ${uspError}`] });
              } else if (usps && usps.length > 0) {
                rows.push({ label: 'Unique Selling Points (USPs)', values: usps });
              }

              // Add Visual-based USPs if available (dict format: {"usp": timestamp})
              const visualUspsDict = ad.metadata?.auto_tagging?.visual_usps as Record<string, number> | undefined;
              if (visualUspsDict && Object.keys(visualUspsDict).length > 0) {
                // Format USPs with timestamps
                const visualUspsWithTimestamps = Object.entries(visualUspsDict).map(
                  ([usp, timestamp]) => `${usp} (${timestamp}s)`
                );
                rows.push({ label: 'Visual-based USPs', values: visualUspsWithTimestamps });
              }

              if (tags) {
                if (typeof tags.cast === 'string') rows.push({ label: 'Cast', values: [tags.cast] });
                if (typeof tags.heroAge === 'string') rows.push({ label: 'Hero Age', values: [tags.heroAge] });
                if (typeof tags.heroGender === 'string') rows.push({ label: 'Hero Gender', values: [tags.heroGender] });
                if (typeof tags.pianoType === 'string') rows.push({ label: 'Piano Type', values: [tags.pianoType] });
                if (typeof tags.appDevice === 'string') rows.push({ label: 'App Device', values: [tags.appDevice] });
              }
              if (licensedSongs.length > 0) rows.push({ label: 'Licensed Song', values: licensedSongs });
              if (meantTo.length > 0) rows.push({ label: 'Meant To', values: meantTo });
              if (audience.length > 0) rows.push({ label: 'Audience', values: audience });
              if (seasonal.length > 0) rows.push({ label: 'Seasonal', values: seasonal });
              if (contentShownInApp.length > 0) rows.push({ label: 'Draw Content', values: contentShownInApp });

              return (
                <Box sx={{ mt: 1 }}>
                  {rows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No tags available</Typography>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={1.5}>
                      {rows.map((row) => (
                        <Box key={row.label} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="subtitle2" sx={{ minWidth: 110 }}>{row.label}</Typography>
                          {row.values.length > 0 ? (
                            row.values.map((val) => (
                              <Chip key={`${row.label}-${val}`} size="small" label={val} />
                            ))
                          ) : (
                            <Chip size="small" label="None" variant="outlined" />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Paper>

          {/* Song Licenses Section */}
          <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Song Licenses</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowLicenseSelector(!showLicenseSelector)}
              >
                {showLicenseSelector ? 'Done' : 'Edit'}
              </Button>
            </Box>

            {licensesLoading ? (
              <CircularProgress size={20} />
            ) : adLicenses.length === 0 && !showLicenseSelector ? (
              <Typography variant="body2" color="text.secondary">No song licenses linked</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {adLicenses.map((license) => (
                  <Box key={license.license_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{license.name}</Typography>
                    <LicenseStatusBadge status={license.status} />
                    {showLicenseSelector && (
                      <IconButton size="small" onClick={() => handleUnlinkLicense(license.license_id)}>
                        <Typography variant="body2" color="error">×</Typography>
                      </IconButton>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {showLicenseSelector && (
              <Box sx={{ mt: 2 }}>
                <LicenseSelector
                  licenseType="song"
                  selectedLicenseIds={adLicenses.map(l => l.license_id)}
                  onSelectionChange={handleLinkLicense}
                />
              </Box>
            )}

            {adLicenses.some(l => l.status === 'expired' || l.status === 'expiring_soon') && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {adLicenses.filter(l => l.status === 'expired').length > 0
                  ? 'This ad has expired licenses'
                  : 'This ad has licenses expiring soon'}
              </Alert>
            )}
          </Paper>

          {/* Transcript Section */}
          {(() => {
            const transcript = getObjectField(ad, 'transcript');
            if (!transcript) return null;
            const statusValue = getObjectField(transcript, 'status');
            const previewValue = getObjectField(transcript, 'preview');
            const languageCodeValue = getObjectField(transcript, 'language_code');
            const links = getObjectField(transcript, 'links');
            const txtUrlValue = getObjectField(links, 'txt_url');
            const status = typeof statusValue === 'string' ? statusValue : undefined;
            const preview = typeof previewValue === 'string' ? previewValue : undefined;
            const languageCode = typeof languageCodeValue === 'string' ? languageCodeValue : undefined;
            const txtUrl = typeof txtUrlValue === 'string' ? txtUrlValue : undefined;
            const handleHeaderToggle = async () => {
              if (!isTranscriptExpanded) {
                if (!fullTranscript && txtUrl) {
                  try {
                    setLoadingTranscript(true);
                    const resp = await fetch(txtUrl);
                    const text = await resp.text();
                    setFullTranscript(text);
                  } catch (e) {
                    setFullTranscript(preview || '');
                  } finally {
                    setLoadingTranscript(false);
                  }
                }
                setIsTranscriptExpanded(true);
              } else {
                setIsTranscriptExpanded(false);
              }
            };
            return (
              <Paper 
                elevation={1} 
                sx={{ 
                  p: 2, 
                  mt: 2,
                  opacity: ad.disabled ? 0.6 : 1,
                  filter: ad.disabled ? 'grayscale(50%)' : 'none',
                  transition: 'all 0.3s ease',
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                  sx={{ cursor: status === 'completed' ? 'pointer' : 'default' }}
                  onClick={status === 'completed' ? handleHeaderToggle : undefined}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="h6">Transcript</Typography>
                    {status === 'completed' && (
                      <ExpandMoreIcon
                        sx={{
                          transition: 'transform 0.2s',
                          transform: isTranscriptExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                        }}
                      />
                    )}
                  </Box>
                  <Box display="flex" gap={1}>
                    {languageCode && (
                      <Chip size="small" label={languageCode} />
                    )}
                    {status && (
                      <Chip size="small" label={status} color={status === 'completed' ? 'success' : status === 'failed' ? 'error' : 'warning'} />
                    )}
                  </Box>
                </Box>
                {status === 'completed' ? (
                  <>
                    {isTranscriptExpanded ? (
                      <>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {fullTranscript ?? preview ?? ''}
                        </Typography>
                        {txtUrl && (
                          <Box mt={1}>
                            <Link href={txtUrl} target="_blank" rel="noreferrer">Download full transcript (txt)</Link>
                          </Box>
                        )}
                      </>
                    ) : (
                      <>
                        {preview ? (
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'pre-wrap',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {preview}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {loadingTranscript ? 'Loading…' : 'Transcript ready.'}
                          </Typography>
                        )}
                      </>
                    )}
                  </>
                ) : status === 'processing' ? (
                  <Typography variant="body2" color="text.secondary">Transcription in progress…</Typography>
                ) : status === 'failed' ? (
                  <Typography variant="body2" color="error">Transcription failed.</Typography>
                ) : null}
              </Paper>
            );
          })()}

          {/* Jobs Section - Only for authenticated users */}
          {isAuthenticated && isAuthorized && (
            <AdJobsSection adId={adId!} />
          )}
        </Grid>

        {/* Ad Details */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 3,
              opacity: ad.disabled ? 0.6 : 1,
              filter: ad.disabled ? 'grayscale(50%)' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Ad Details
            </Typography>
            
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Production ID" 
                  secondary={ad.production_id} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Creative Number" 
                  secondary={ad.creative_number} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <AspectRatioIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Aspect Ratio" 
                  secondary={ad.ratio} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <ScheduleIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Duration" 
                  secondary={formatDuration(ad.duration)} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <StorageIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="File Size" 
                  secondary={formatFileSize(ad.file_size)} 
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Created By" 
                  secondary={ad.created_by} 
                />
              </ListItem>
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle2" gutterBottom>
              Watchers
            </Typography>
            {ad.watchers.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                {ad.watchers.map((watcher) => (
                  <Chip
                    key={watcher}
                    label={watcher}
                    size="small"
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No watchers assigned
              </Typography>
            )}
            
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadFile}
              >
                Download File
              </Button>

              {isHtmlAd(ad) && (
                <Typography variant="body2" color="text.secondary">
                  YouTube, Meta, and AppLovin uploads are not available for HTML creatives.
                </Typography>
              )}

              {/* USP Generation button - visible to all authenticated users */}
              {isAuthenticated && ad.metadata?.transcription?.status === 'completed' && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SmartToyIcon />}
                  onClick={handleGenerateUsps}
                  color="primary"
                  disabled={loading}
                >
                  {loading ? 'Generating...' : 'Generate USPs'}
                </Button>
              )}

              {/* Debug info button - visible to authenticated users */}
              {isAuthenticated && isAuthorized && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<StorageIcon />}
                  onClick={handleOpenDebugModal}
                  color="secondary"
                >
                  Debug Info
                </Button>
              )}

              {/* Additional actions only for authenticated users */}
              {isAuthenticated && isAuthorized && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleOpenEditModal}
                  >
                    Edit Metadata
                  </Button>

                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={handleToggleDisabled}
                    disabled={isTogglingDisabled}
                  >
                    {isTogglingDisabled ? (
                      <CircularProgress size={16} />
                    ) : ad.disabled ? (
                      'Enable Ad'
                    ) : (
                      'Disable Ad'
                    )}
                  </Button>
                </>
              )}

              {/* Platform uploads only for authenticated users */}
              {isAuthenticated && isAuthorized && (
                <>
                  {/* Meta Ad Library Upload Section */}
                  {META_UPLOAD_ENABLED && !isHtmlAd(ad) && (ad.meta_ad_id ? (
                    // Already uploaded to Meta - show status
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Uploaded to Meta Ad Library"
                        color="success"
                        size="small"
                      />
                    </Box>
                  ) : (
                    // Not yet uploaded - show upload button
                    <Button
                      variant="contained"
                      startIcon={metaUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                      size="small"
                      sx={{
                        backgroundColor: '#1877F2',
                        '&:hover': {
                          backgroundColor: '#166FE5',
                        }
                      }}
                      disabled={metaUploading || ad.disabled}
                      onClick={handleMetaUpload}
                    >
                      <FacebookIcon sx={{ mr: 1 }} />
                      {metaUploading ? 'Uploading...' : 'Upload to Meta Ad Library'}
                    </Button>
                  ))}

                  {/* AppLovin Upload Section */}
                  {!isHtmlAd(ad) && (ad.applovin_creative_set_id ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Uploaded to AppLovin"
                        color="success"
                        size="small"
                      />
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={applovinUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                      size="small"
                      sx={{
                        backgroundColor: '#00B460',
                        '&:hover': {
                          backgroundColor: '#009950',
                        }
                      }}
                      disabled={applovinUploading || ad.disabled}
                      onClick={() => setApplovinDialogOpen(true)}
                    >
                      {applovinUploading ? 'Uploading...' : 'Upload to AppLovin'}
                    </Button>
                  ))}
                </>
              )}
            </Box>

            {isAuthenticated && isAuthorized && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box component="section" role="region" aria-labelledby="google-actions-heading">
                  <Typography id="google-actions-heading" variant="subtitle2" gutterBottom>
                    Google Actions
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {/* YouTube Upload Section */}
                    {!isHtmlAd(ad) && (ad.youtube_video_id ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Uploaded to YouTube"
                          color="success"
                          size="small"
                        />
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Button
                            variant="outlined"
                            startIcon={<YouTubeIcon />}
                            endIcon={<LaunchIcon />}
                            size="small"
                            color="error"
                            href={generateYouTubeUrl(ad.youtube_video_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ flex: 1 }}
                          >
                            View on YouTube
                          </Button>
                          <Tooltip title="Copy YouTube ID">
                            <IconButton
                              aria-label="Copy YouTube ID"
                              size="small"
                              color="error"
                              onClick={() => copyToClipboard(ad.youtube_video_id!, 'YouTube ID')}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={youtubeUploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                        size="small"
                        color="error"
                        disabled={youtubeUploading || ad.disabled}
                        onClick={handleYouTubeUpload}
                      >
                        {youtubeUploading ? 'Uploading...' : 'Upload to YouTube'}
                      </Button>
                    ))}

                    {/* Google Ads library status, upload, and placement entry point */}
                    {GOOGLE_ADS_UPLOAD_ENABLED && (ad.google_ads_asset_id ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="In Google Ads Library"
                          color="success"
                          size="small"
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                            Google Ads Asset ID: {ad.google_ads_asset_id}
                          </Typography>
                          <Tooltip title="Copy Google Ads Asset ID">
                            <IconButton
                              aria-label="Copy Google Ads Asset ID"
                              size="small"
                              onClick={() => copyToClipboard(ad.google_ads_asset_id!, 'Google Ads Asset ID')}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setGoogleAdsAssociationOpen(true)}
                          disabled={ad.disabled}
                        >
                          Associate with Google ad groups
                        </Button>
                      </Box>
                    ) : (isHtmlAd(ad) || isImageAd(ad) || (isVideoAd(ad) && Boolean(ad.youtube_video_id))) ? (
                      <Button
                        variant="contained"
                        startIcon={googleAdsAdding ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                        size="small"
                        data-testid="google-ads-library-upload-button"
                        sx={{
                          backgroundColor: '#4285F4',
                          '&:hover': {
                            backgroundColor: '#3367D6',
                          }
                        }}
                        disabled={googleAdsAdding || ad.disabled}
                        onClick={handleAddToGoogleAds}
                      >
                        {googleAdsAdding ? 'Uploading...' : 'Upload to Google Ads library'}
                      </Button>
                    ) : null)}
                  </Box>


                  {/* Persistent surface: an orphaned asset id must survive clicks and timeouts. */}
                  {googleAdsError && (
                    <Alert
                      severity="error"
                      data-testid="google-ads-error"
                      onClose={() => setGoogleAdsError(null)}
                      sx={{ mt: 1, overflowWrap: 'anywhere' }}
                    >
                      {googleAdsError.message}
                      {googleAdsError.orphanedAssetId && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
                            Orphaned Google Ads Asset ID: {googleAdsError.orphanedAssetId}
                          </Typography>
                          <Tooltip title="Copy orphaned Google Ads Asset ID">
                            <IconButton
                              aria-label="Copy orphaned Google Ads Asset ID"
                              size="small"
                              onClick={() => copyToClipboard(googleAdsError.orphanedAssetId!, 'Orphaned Google Ads Asset ID')}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Alert>
                  )}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {ad.google_ads_asset_id && (
        <GoogleAdsAssociationDialog
          adId={ad.ad_id}
          open={googleAdsAssociationOpen}
          onClose={() => setGoogleAdsAssociationOpen(false)}
          onCompleted={(response) => {
            const savedAdId = ad.ad_id;
            if (!mountedRef.current || currentAdIdRef.current !== savedAdId
              || routeGenerationRef.current !== routeGeneration) return;
            if (
              response.status === 'partial_success'
              || response.failed_ad_groups.length > 0
              || response.failed_removed_ad_groups.length > 0
            ) return;
            const refreshVersion = ++googleAdsRequestVersionRef.current;

            const associated = response.successful_ad_groups.length;
            const removed = response.removed_ad_groups.length;
            const existing = response.already_uploaded_ad_groups.length;
            // Placements in `already_removed_ad_groups` were absent in Google already, so nothing
            // was detached for them — report them apart from the removals we actually performed.
            const alreadyAbsent = response.already_removed_ad_groups?.length ?? 0;
            const counts = `${associated} added, ${removed} removed, ${existing} already present, ${alreadyAbsent} already absent`;

            setGoogleAdsAssociationOpen(false);
            setSuccessMessage(`Google Ads associations updated: ${counts}.`);

            // Recording the placement advances the ad row's `updated_at`, which the Debug Info
            // modal dumps, so this page holds a stale ad as soon as the save lands. Refresh out of
            // band and only on a full success: the placements already happened, so a refresh
            // failure must not be reported as a placement failure, and a partial save leaves the
            // dialog open to own that surface itself.
            const refreshAd = async () => {
              try {
                const refreshedAd = await apiService.getAd(savedAdId);
                if (mountedRef.current && currentAdIdRef.current === savedAdId
                  && routeGenerationRef.current === routeGeneration
                  && googleAdsRequestVersionRef.current === refreshVersion) setAd(refreshedAd);
              } catch (refreshError: unknown) {
                console.error(
                  'Google Ads associations updated for ad %s but refreshing the ad failed:',
                  savedAdId, refreshError,
                );
              }
            };
            refreshAd();
          }}
        />
      )}

      {/* Edit Metadata Modal - Only for authenticated users */}
      {isAuthenticated && isAuthorized && (
        <Dialog open={editModalOpen} onClose={handleCloseEditModal} maxWidth="md" fullWidth>
        <DialogTitle>Edit Ad Metadata</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Language */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="language"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth required error={!!errors.language}>
                      <InputLabel>Language</InputLabel>
                      <Select {...field} label="Language">
                        {AD_LANGUAGE_OPTIONS.map((option) => (
                          <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>
                        ))}
                      </Select>
                      {errors.language && (
                        <Typography variant="caption" color="error">
                          {errors.language.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Cast */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="cast"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Cast</InputLabel>
                      <Select {...field} label="Cast" value={field.value || ''}>
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Kids">Kids</MenuItem>
                        <MenuItem value="Couple">Couple</MenuItem>
                        <MenuItem value="Solo actor">Solo actor</MenuItem>
                        <MenuItem value="No Human actor">No Human actor</MenuItem>
                        <MenuItem value="Human with Prompt">Human with Prompt</MenuItem>
                        <MenuItem value="hand only">hand only</MenuItem>
                        <MenuItem value="Family">Family</MenuItem>
                        <MenuItem value="unknown">unknown</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Hero Age */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="heroAge"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Hero Age</InputLabel>
                      <Select {...field} label="Hero Age" value={field.value || ''}>
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Senior">Senior</MenuItem>
                        <MenuItem value="Adult">Adult</MenuItem>
                        <MenuItem value="Teenage">Teenage</MenuItem>
                        <MenuItem value="Kid">Kid</MenuItem>
                        <MenuItem value="unknown">unknown</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Hero Gender */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="heroGender"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Hero Gender</InputLabel>
                      <Select {...field} label="Hero Gender" value={field.value || ''}>
                        <MenuItem value="">None</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="other">other</MenuItem>
                        <MenuItem value="unknown">unknown</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Meant To */}
              <Grid item xs={12}>
                <Controller
                  name="meantTo"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      {...field}
                      multiple
                      freeSolo
                      options={getMeantToPlatforms()}
                      value={field.value}
                      onChange={(_, value) => field.onChange(value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Meant To" placeholder="Add tags (press Enter)" />
                      )}
                    />
                  )}
                />
              </Grid>

              {/* Watchers */}
              <Grid item xs={12}>
                <Controller
                  name="watchers"
                  control={control}
                  render={({ field }) => (
                    <SlackUserSelector
                      selectedUsers={field.value}
                      onUsersChange={field.onChange}
                      label="Watchers"
                      placeholder="Search for team members to watch this ad..."
                    />
                  )}
                />
              </Grid>
            </Grid>

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditModal} disabled={isUpdating}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(handleUpdateMetadata)}
            variant="contained"
            disabled={isUpdating}
            startIcon={isUpdating ? <CircularProgress size={16} /> : undefined}
          >
            {isUpdating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      )}

      {/* Debug Info Modal - Only for authenticated users */}
      {isAuthenticated && isAuthorized && (
        <Dialog
          open={isDebugModalOpen}
          onClose={handleCloseDebugModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: { height: '80vh' }
          }}
        >
          <DialogTitle>
            Debug Information - Ad {ad?.ad_id}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              {debugData ? (
                <pre style={{
                  fontSize: '12px',
                  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(debugData, null, 2)}
                </pre>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No debug data available
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDebugModal}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      <ApplovinUploadDialog
        open={applovinDialogOpen}
        onClose={() => setApplovinDialogOpen(false)}
        onConfirm={handleApplovinUpload}
        adCount={1}
        isUploading={applovinUploading}
      />
    </Container>
  );
};

export default AdDetailPage; 